/*
Copyright 2026 Element Creations Ltd.
SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/
import {
  useEffect,
  useState,
  useRef,
  useSyncExternalStore,
  type CSSProperties,
} from "react";
import { type TrackReferenceOrPlaceholder } from "@livekit/components-core";
import { Track, type Room } from "livekit-client";
import {
  orientationRevision,
  subscribeOrientation,
  presentationRotation,
  installScreenShareOrientation,
} from "./screenShareOrientation";

export function rotatedVideoStyle(
  rotation: number,
  width: number,
  height: number,
  boxWidth: number,
  boxHeight: number,
): CSSProperties | undefined {
  if (!rotation || Math.min(width, height, boxWidth, boxHeight) <= 0) return;
  const scale = Math.min(boxWidth / height, boxHeight / width);
  return {
    position: "absolute",
    left: "50%",
    top: "50%",
    width: width * scale,
    height: height * scale,
    transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
    objectFit: "contain",
  };
}
export function useScreenShareVideoStyle(
  video: TrackReferenceOrPlaceholder | undefined,
) {
  const [element, setElement] = useState<HTMLVideoElement | null>(null);
  const [geometry, setGeometry] = useState({
    width: 0,
    height: 0,
    boxWidth: 0,
    boxHeight: 0,
  });
  useSyncExternalStore(subscribeOrientation, orientationRevision, () => 0);
  const screenShare = video?.source === Track.Source.ScreenShare;
  useEffect(() => {
    if (!element || !screenShare) return;
    const update = (): void => {
      const box = element.parentElement?.getBoundingClientRect();
      const next = {
        width: element.videoWidth,
        height: element.videoHeight,
        boxWidth: box?.width ?? 0,
        boxHeight: box?.height ?? 0,
      };
      setGeometry((old) =>
        Object.keys(next).every(
          (key) =>
            old[key as keyof typeof old] === next[key as keyof typeof next],
        )
          ? old
          : next,
      );
    };
    const observer =
      typeof ResizeObserver === "undefined"
        ? undefined
        : new ResizeObserver(update);
    if (element.parentElement) observer?.observe(element.parentElement);
    window.addEventListener("resize", update);
    element.addEventListener("loadedmetadata", update);
    element.addEventListener("resize", update);
    update();
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", update);
      element.removeEventListener("loadedmetadata", update);
      element.removeEventListener("resize", update);
    };
  }, [element, screenShare]);
  const rotation = screenShare
    ? presentationRotation(
        video?.participant,
        video?.publication?.trackSid,
        geometry.width,
        geometry.height,
      )
    : 0;
  return {
    ref: setElement,
    rotation,
    style: rotatedVideoStyle(
      rotation,
      geometry.width,
      geometry.height,
      geometry.boxWidth,
      geometry.boxHeight,
    ),
  };
}

export function useScreenShareOrientationRooms(rooms: Room[]): void {
  const installed = useRef(new Map<Room, () => void>());
  useEffect(() => {
    const current = new Set(
      import.meta.env.VITE_ENABLE_SCREEN_ORIENTATION === "true" ? rooms : [],
    );
    for (const [room, dispose] of installed.current)
      if (!current.has(room)) {
        dispose();
        installed.current.delete(room);
      }
    for (const room of current)
      if (!installed.current.has(room))
        installed.current.set(room, installScreenShareOrientation(room));
  }, [rooms]);
  useEffect(
    () => () => {
      installed.current.forEach((dispose) => dispose());
      installed.current.clear();
    },
    [],
  );
}
